import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, FileArchive } from "lucide-react";
import { useEquipmentDetails, useEquipmentCertificate } from "@/hooks";
import { Separator } from "@/components/ui/separator";
import {
  EquipmentBadge,
  EquipmentBadgeVariants,
} from "@/components/equipment-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function EquipmentDetailsPage() {
  const { id } = useParams();
  const { data: equipment, isLoading } = useEquipmentDetails(id ?? "");
  const { mutateAsync: getEquipmentCertificate } = useEquipmentCertificate();

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="container mx-auto pb-8 grid gap-3">
          <div className="flex items-center gap-0">
            <Link
              to="/equipos"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <ChevronLeft className="size-4" />
              Regresar
            </Link>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold">Detalles del Equipo</h1>
              <p className="text-xs text-muted-foreground">
                Información detallada del equipo seleccionado
              </p>
            </div>
            <Button asChild>
              <Link to={`/equipos/${id}/editar`}>Editar equipo</Link>
            </Button>
          </div>
          <Separator />

          {equipment && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Tipo de Equipo
                  </h3>
                  <EquipmentBadge
                    variant={
                      equipment.details.equipmentType as EquipmentBadgeVariants
                    }
                  />
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Número de Serie
                  </h3>
                  <p className="font-medium">
                    {equipment.details.serialNumber}
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Marca
                  </h3>
                  <p className="font-medium">{equipment.details.brand}</p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Modelo
                  </h3>
                  <p className="font-medium">{equipment.details.model}</p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Fecha de Calibración
                  </h3>
                  <p className="font-medium">
                    {equipment.details.calibratedAt
                      ? equipment.details.calibratedAt
                      : "No especificada"}
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Próxima Calibración
                  </h3>
                  <p className="font-medium">
                    {equipment.details.nextCalibration
                      ? equipment.details.nextCalibration
                      : "No especificada"}
                  </p>
                </div>
              </div>

              {equipment.details.certificatePath && (
                <div className="pt-2">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">
                    Certificado
                  </h3>
                  <Button
                    variant="success"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={async () => {
                      const url = await getEquipmentCertificate({
                        equipmentId: equipment.details.id,
                      });
                      window.open(url, "_blank");
                    }}
                  >
                    <FileArchive className="size-4" />
                    Descargar certificado
                  </Button>
                </div>
              )}

              {equipment.correctionIntervals.length > 0 &&
                equipment.details.equipmentType === "Luxometro" && (
                  <div className="pt-2">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">
                      Correcciones
                    </h3>
                    <div className="rounded-md border">
                      <Table className="text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              <p>Minimo</p>
                              <p> (lux)</p>
                            </TableHead>
                            <TableHead>
                              <p>Maximo</p>
                              <p> (lux)</p>
                            </TableHead>
                            <TableHead>
                              <p>Corrección</p>
                              <p> (Aditiva)</p>
                            </TableHead>
                            <TableHead>
                              <p>Corrección</p>
                              <p> (Multiplicativa)</p>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {equipment.correctionIntervals.map(
                            (interval, index) => (
                              <TableRow key={index} className="font-mono">
                                <TableCell>{interval.start}</TableCell>
                                <TableCell>{interval.end}</TableCell>
                                <TableCell>{interval.offset}</TableCell>
                                <TableCell>{interval.scalar}</TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

              <div className="flex justify-end gap-2 mt-4"></div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
